import type { CSSProperties } from 'react'

/** 页内嵌板模板（含条漫、跨格） */
export type ComicPageLayoutTemplate =
  | '1x1'
  | '2x1'
  | '1x2'
  | '2x2'
  | '1x3'
  | '1x4'
  | '2x2_span_top'
  | '2x2_span_left'
  | '2x2_span_right'
  | '2x2_span_bottom'

export type ComicLayoutSlotDef = {
  col: number
  row: number
  colSpan: number
  rowSpan: number
}

export type ComicLayoutGroup = 'standard' | 'span' | 'webtoon'

export type ComicPageLayoutDefinition = {
  value: ComicPageLayoutTemplate
  label: string
  hint: string
  maxPanels: number
  cols: number
  rows: number
  slots: ComicLayoutSlotDef[]
  group: ComicLayoutGroup
  /** 兼容旧 grid 类（规则矩形格） */
  gridClass: string
}

function rectangularSlots(cols: number, rows: number): ComicLayoutSlotDef[] {
  const slots: ComicLayoutSlotDef[] = []
  for (let row = 1; row <= rows; row += 1) {
    for (let col = 1; col <= cols; col += 1) {
      slots.push({ col, row, colSpan: 1, rowSpan: 1 })
    }
  }
  return slots
}

export const COMIC_PAGE_LAYOUT_DEFINITIONS: ComicPageLayoutDefinition[] = [
  {
    value: '1x1',
    label: '单格',
    hint: '全页一格',
    maxPanels: 1,
    cols: 1,
    rows: 1,
    slots: rectangularSlots(1, 1),
    group: 'standard',
    gridClass: 'grid-cols-1',
  },
  {
    value: '2x1',
    label: '两格横',
    hint: '左右并列',
    maxPanels: 2,
    cols: 2,
    rows: 1,
    slots: rectangularSlots(2, 1),
    group: 'standard',
    gridClass: 'grid-cols-2',
  },
  {
    value: '1x2',
    label: '两格竖',
    hint: '上下叠放',
    maxPanels: 2,
    cols: 1,
    rows: 2,
    slots: rectangularSlots(1, 2),
    group: 'standard',
    gridClass: 'grid-cols-1 grid-rows-2',
  },
  {
    value: '2x2',
    label: '四格',
    hint: '经典四格',
    maxPanels: 4,
    cols: 2,
    rows: 2,
    slots: rectangularSlots(2, 2),
    group: 'standard',
    gridClass: 'grid-cols-2 grid-rows-2',
  },
  {
    value: '2x2_span_top',
    label: '上大下双',
    hint: '顶格横跨两列',
    maxPanels: 3,
    cols: 2,
    rows: 2,
    slots: [
      { col: 1, row: 1, colSpan: 2, rowSpan: 1 },
      { col: 1, row: 2, colSpan: 1, rowSpan: 1 },
      { col: 2, row: 2, colSpan: 1, rowSpan: 1 },
    ],
    group: 'span',
    gridClass: 'grid-cols-2 grid-rows-2',
  },
  {
    value: '2x2_span_bottom',
    label: '上双下大',
    hint: '底格横跨两列',
    maxPanels: 3,
    cols: 2,
    rows: 2,
    slots: [
      { col: 1, row: 1, colSpan: 1, rowSpan: 1 },
      { col: 2, row: 1, colSpan: 1, rowSpan: 1 },
      { col: 1, row: 2, colSpan: 2, rowSpan: 1 },
    ],
    group: 'span',
    gridClass: 'grid-cols-2 grid-rows-2',
  },
  {
    value: '2x2_span_left',
    label: '左大右双',
    hint: '左格纵跨两行',
    maxPanels: 3,
    cols: 2,
    rows: 2,
    slots: [
      { col: 1, row: 1, colSpan: 1, rowSpan: 2 },
      { col: 2, row: 1, colSpan: 1, rowSpan: 1 },
      { col: 2, row: 2, colSpan: 1, rowSpan: 1 },
    ],
    group: 'span',
    gridClass: 'grid-cols-2 grid-rows-2',
  },
  {
    value: '2x2_span_right',
    label: '左双右大',
    hint: '右格纵跨两行',
    maxPanels: 3,
    cols: 2,
    rows: 2,
    slots: [
      { col: 1, row: 1, colSpan: 1, rowSpan: 1 },
      { col: 1, row: 2, colSpan: 1, rowSpan: 1 },
      { col: 2, row: 1, colSpan: 1, rowSpan: 2 },
    ],
    group: 'span',
    gridClass: 'grid-cols-2 grid-rows-2',
  },
  {
    value: '1x3',
    label: '条漫三格',
    hint: '竖向三连格',
    maxPanels: 3,
    cols: 1,
    rows: 3,
    slots: rectangularSlots(1, 3),
    group: 'webtoon',
    gridClass: 'grid-cols-1 grid-rows-3',
  },
  {
    value: '1x4',
    label: '条漫四格',
    hint: '竖向四连格',
    maxPanels: 4,
    cols: 1,
    rows: 4,
    slots: rectangularSlots(1, 4),
    group: 'webtoon',
    gridClass: 'grid-cols-1 grid-rows-4',
  },
]

export type ComicPageLayoutOption = Pick<
  ComicPageLayoutDefinition,
  'value' | 'label' | 'hint' | 'maxPanels' | 'gridClass'
>

export const COMIC_PAGE_LAYOUT_OPTIONS: ComicPageLayoutOption[] =
  COMIC_PAGE_LAYOUT_DEFINITIONS.map(({ value, label, hint, maxPanels, gridClass }) => ({
    value,
    label,
    hint,
    maxPanels,
    gridClass,
  }))

export const COMIC_LAYOUT_GROUPS: { key: ComicLayoutGroup; label: string }[] = [
  { key: 'standard', label: '常规' },
  { key: 'span', label: '跨格' },
  { key: 'webtoon', label: '条漫' },
]

export const COMIC_PAGE_LAYOUT_DEFAULT: ComicPageLayoutTemplate = '2x2'

const LAYOUT_BY_VALUE = new Map(
  COMIC_PAGE_LAYOUT_DEFINITIONS.map((d) => [d.value, d] as const),
)

export function getPageLayoutDefinition(
  layout: ComicPageLayoutTemplate,
): ComicPageLayoutDefinition {
  return LAYOUT_BY_VALUE.get(layout) ?? LAYOUT_BY_VALUE.get(COMIC_PAGE_LAYOUT_DEFAULT)!
}

export function normalizePageLayout(raw: unknown): ComicPageLayoutTemplate {
  if (typeof raw === 'string' && LAYOUT_BY_VALUE.has(raw as ComicPageLayoutTemplate)) {
    return raw as ComicPageLayoutTemplate
  }
  return COMIC_PAGE_LAYOUT_DEFAULT
}

export function getPageLayoutOption(layout: ComicPageLayoutTemplate): ComicPageLayoutOption {
  const def = getPageLayoutDefinition(layout)
  return {
    value: def.value,
    label: def.label,
    hint: def.hint,
    maxPanels: def.maxPanels,
    gridClass: def.gridClass,
  }
}

export function pageLayoutGridClass(layout: ComicPageLayoutTemplate): string {
  return getPageLayoutDefinition(layout).gridClass
}

/** 画布整页预览外框比例：按版式而非画册单页比例，避免竖版 3:4 在窄屏上过高 */
export function wholePagePreviewAspectClass(layout: ComicPageLayoutTemplate): string {
  switch (layout) {
    case '2x1':
      return 'aspect-video'
    case '1x2':
    case '1x3':
    case '1x4':
      return 'aspect-[9/16]'
    case '2x2':
    case '2x2_span_top':
    case '2x2_span_bottom':
    case '2x2_span_left':
    case '2x2_span_right':
      return 'aspect-square'
    default:
      return 'aspect-[3/4]'
  }
}

export function layoutSlotCss(slot: ComicLayoutSlotDef): CSSProperties {
  return {
    gridColumn: `${slot.col} / span ${slot.colSpan}`,
    gridRow: `${slot.row} / span ${slot.rowSpan}`,
  }
}

export function layoutGridContainerStyle(layout: ComicPageLayoutTemplate): CSSProperties {
  const def = getPageLayoutDefinition(layout)
  return {
    gridTemplateColumns: `repeat(${def.cols}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${def.rows}, minmax(0, 1fr))`,
  }
}

/** 槽位在画布上的像素区域（生图、导出、气泡叠字共用） */
export function layoutSlotRect(
  slot: ComicLayoutSlotDef,
  cellW: number,
  cellH: number,
  gap: number,
  originX = 0,
  originY = 0,
): { x: number; y: number; w: number; h: number } {
  const x = originX + (slot.col - 1) * (cellW + gap)
  const y = originY + (slot.row - 1) * (cellH + gap)
  const w = slot.colSpan * cellW + (slot.colSpan - 1) * gap
  const h = slot.rowSpan * cellH + (slot.rowSpan - 1) * gap
  return { x, y, w, h }
}

export function layoutCanvasSize(
  layout: ComicPageLayoutTemplate,
  cellW: number,
  cellH: number,
  gap: number,
): { width: number; height: number } {
  const { cols, rows } = getPageLayoutDefinition(layout)
  return {
    width: cols * cellW + (cols - 1) * gap,
    height: rows * cellH + (rows - 1) * gap,
  }
}

export function pageHasRoom(layout: ComicPageLayoutTemplate, panelCount: number): boolean {
  return panelCount < getPageLayoutDefinition(layout).maxPanels
}

export function pageMaxPanels(layout: ComicPageLayoutTemplate): number {
  return getPageLayoutDefinition(layout).maxPanels
}

export function pagePanelCapacity(page: { layout: ComicPageLayoutTemplate; panelIds: string[] }): {
  count: number
  max: number
  overflow: number
} {
  const max = pageMaxPanels(page.layout)
  const count = page.panelIds.length
  return { count, max, overflow: Math.max(0, count - max) }
}

export function formatPagePanelCapacity(page: {
  layout: ComicPageLayoutTemplate
  panelIds: string[]
}): string {
  const { count, max } = pagePanelCapacity(page)
  return `${count}/${max}`
}

/** @deprecated 使用 getPageLayoutDefinition；保留给旧调用方 */
export function layoutGridDimensions(layout: ComicPageLayoutTemplate): {
  cols: number
  rows: number
} {
  const def = getPageLayoutDefinition(layout)
  return { cols: def.cols, rows: def.rows }
}

export function layoutUsesIrregularSlots(layout: ComicPageLayoutTemplate): boolean {
  return getPageLayoutDefinition(layout).group === 'span'
}

export function layoutHorizontalDividerCount(layout: ComicPageLayoutTemplate): number {
  if (layoutUsesIrregularSlots(layout)) return 0
  const { cols, rows } = layoutGridDimensions(layout)
  if (rows <= 1) return 0
  return cols === 1 ? rows - 1 : 1
}

export function layoutVerticalDividerCount(layout: ComicPageLayoutTemplate): number {
  if (layoutUsesIrregularSlots(layout)) return 0
  return layoutGridDimensions(layout).cols >= 2 ? 1 : 0
}

export function isWebtoonLayout(layout: ComicPageLayoutTemplate): boolean {
  return getPageLayoutDefinition(layout).group === 'webtoon'
}

export function episodeIsWebtoonEpisode(pages: { layout: ComicPageLayoutTemplate }[]): boolean {
  return pages.length > 0 && pages.every((p) => isWebtoonLayout(p.layout))
}

export function layoutsForGroup(group: ComicLayoutGroup): ComicPageLayoutDefinition[] {
  return COMIC_PAGE_LAYOUT_DEFINITIONS.filter((d) => d.group === group)
}
